import { IBaseStore } from '../types'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import Api from '../../api'
import { EZuntaInvoiceStatus, EZuntaInvoiceType, IBillingBatchHistory, IBillingHistory, IUser, DateRange, Dictionary } from '@zunta/configurations'

export interface IBillingState {
  byDateStatistics: Array<{ date: string, cost: number, profit: number, gross: number }>;
  clientsByModulesStatistics: Array<{ date: string, mpa: number, mp: number, p: number }>;
  revenueByModulesStatistics: Array<{ date: string, cost: { monthly: number, payments: number, mailing: number }, profit: { monthly: number, payments: number, mailing: number } }>;
  chainsStatistics: Array<{ date: string, gained: string, lost: number, current: number }>;
  facilitiesStatistics: Array<{ date: string, gained: string, lost: number, current: number }>;
  salesRepsData: Array<{ user: IUser, statistics: any }>;
  salesRepStatistics: any;
  facilitiesWithCommissionSettings: any[];
  facilitiesWithCommissionSettingsTotal: number;
  billingBatchHistory: IBillingBatchHistory[];
}

export interface IBillingStore extends IBaseStore {
  state: IBillingState
}

@Module({
  name: 'billing',
  namespaced: true
})
export class BillingModule extends VuexModule {
  byDateStatistics: Array<{ date: string, cost: number, profit: number, gross: number }> = [];
  clientsByModulesStatistics: Array<{ date: string, mpa: number, mp: number, p: number }> = [];
  revenueByModulesStatistics: Array<{ date: string, cost: { monthly: number, payments: number, mailing: number }, profit: { monthly: number, payments: number, mailing: number } }> = []
  chainsStatistics: Array<{ date: string, gained: string, lost: number, current: number }> = [];
  facilitiesStatistics: Array<{ date: string, gained: string, lost: number, current: number }> = [];
  salesRepsData: Array<{ user: IUser, statistics: any }> = [];

  salesRepStatistics: any = null;
  facilitiesWithCommissionSettingsTotal: number = 0;
  facilitiesWithCommissionSettings: any[] = [];

  billingBatchHistory: IBillingBatchHistory[] = [];

  @Mutation
  SET_BILLING_BATCH_HISTORY (history: IBillingBatchHistory[]) {
    this.billingBatchHistory = history
  }

  @Mutation
  SET_FACILITIES_COMMISSION_SETTINGS_TOTAL (total: number) {
    this.facilitiesWithCommissionSettingsTotal = total
  }

  @Mutation
  SET_FACILITIES_COMMISSION_SETTINGS (items: any[]) {
    this.facilitiesWithCommissionSettings = items
  }

  @Mutation
  SET_SALES_REP_STATISTICS (data: any) {
    this.salesRepStatistics = data
  }

  @Mutation
  SET_SALES_REPS_DATA (data: Array<{ user: IUser, statistics: any }>) {
    this.salesRepsData = data
  }

  @Mutation
  SET_CHAINS_STATISTICS (data: Array<{ date: string, gained: string, lost: number, current: number }>) {
    this.chainsStatistics = data
  }

  @Mutation
  SET_FACILITIES_STATISTICS (data: Array<{ date: string, gained: string, lost: number, current: number }>) {
    this.facilitiesStatistics = data
  }

  @Mutation
  SET_REVENUE_BY_MODULES_STATISTICS (data: Array<{ date: string, cost: { monthly: number, payments: number, mailing: number }, profit: { monthly: number, payments: number, mailing: number } }>) {
    this.revenueByModulesStatistics = data
  }

  @Mutation
  SET_CLIENTS_BY_MODULES_STATISTICS (data: Array<{ date: string, mpa: number, mp: number, p: number }>) {
    this.clientsByModulesStatistics = data
  }

  @Mutation
  SET_BY_DATE_STATISTICS (data: Array<{ date: string, cost: number, profit: number, gross: number }>) {
    this.byDateStatistics = data
  }

  @Action
  async fetchBillingBatchHistory (batchId: number | string) {
    const history = await Api.billing.getBillingBatchHistory(batchId)
    this.SET_BILLING_BATCH_HISTORY(history)
  }

  @Action
  async addBillingBatchHistory (data: Partial<IBillingBatchHistory>) {
    await Api.billing.addBillingBatchHistory(data)
  }

  @Action
  async fetchByDateStatistics (input: { months: number, skip: number }) {
    const result = await Api.billing.getBillingStatistics(input.months, input.skip)
    this.SET_BY_DATE_STATISTICS(result)
  }

  @Action
  async fetchClientsByModuleStatistics (input: { months: number, skip: number }) {
    const result = await Api.billing.getClientsByModulesStatistics(input.months, input.skip)
    this.SET_CLIENTS_BY_MODULES_STATISTICS(result)
  }

  @Action
  async fetchRevenueByModulesStatistics (input: { months: number, skip: number }) {
    const result = await Api.billing.getRevenueByModulesStatistics(input.months, input.skip)
    this.SET_REVENUE_BY_MODULES_STATISTICS(result)
  }

  @Action
  async fetchChainsStatistics (input: { months: number, skip: number }) {
    const result = await Api.billing.getChainsStatistics(input.months, input.skip)
    this.SET_CHAINS_STATISTICS(result)
  }
  @Action
  async fetchFacilitiesStatistics (input: { months: number, skip: number }) {
    const result = await Api.billing.getFacilitiesStatistics(input.months, input.skip)
    this.SET_FACILITIES_STATISTICS(result)
  }

  @Action
  async updateBatchInvoiceTypes (input: { batchId: number | string, invoiceTypes: EZuntaInvoiceType[], echeckBatchCloseTimeOptions: Dictionary<DateRange<string>> | null }) {
    await Api.billing.updateBatchInvoiceTypes(input.batchId, input.invoiceTypes, input.echeckBatchCloseTimeOptions)
  }

  @Action
  async fetchSalesRepsData () {
    const result = await Api.billing.getSapesRepsList()
    this.SET_SALES_REPS_DATA(result)
  }

  @Action
  async fetchSalesRepStatistics (input: { userId: number, months: number, skip: number }) {
    const result = await Api.billing.getSalesRepStatistics(input.userId, input.months, input.skip)
    this.SET_SALES_REP_STATISTICS(result)
  }

  @Action
  async fetchFacilitiesWithCommissionSettings (filters: any) {
    const { facilities, total } = await Api.facility.getFacilityList(filters)
    this.SET_FACILITIES_COMMISSION_SETTINGS_TOTAL(total)
    this.SET_FACILITIES_COMMISSION_SETTINGS(facilities)
  }
}
